<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import SecurityUserAvatar from "@/components/security/SecurityUserAvatar.vue";
import { saveUserListState, restoreUserListState } from "./securityUserListState";
import dayjs from "dayjs";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import Notification from "@/utils/plugins/NotificationService";
import UsersExtranetService from "@/api/services/usersExtranet";

const router = useRouter();
const page = ref(1);
const itemsPerPage = ref(10);
const filters = ref({ search: "", client: "", status: "active", role: "", lastLogin: "" });

const rawUsers = ref([]);

const tones = ["#b88761", "#96634d", "#a69964", "#628db2", "#9d705b", "#bb9564", "#6d95b3", "#b77867", "#8d7658", "#ad668b", "#9871ad", "#4f4f4f"];
const toneFor = (id) => tones[id % tones.length];
const initialsOf = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
const normalize = (value) => (value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const loadUsers = () => {
  // `roles` es una columna json (no filtrable con SearchFilter estándar), así que el
  // filtro de rol se resuelve en cliente — se carga todo (~15 registros) y se filtra/pagina
  // aquí, igual que en PermissionCatalog.vue.
  UsersExtranetService.getUsers({ itemsPerPage: 300 }).then((res) => {
    if (!res) return;
    rawUsers.value = res.data["hydra:member"] ?? [];
  });
};

const users = computed(() => rawUsers.value.map((user) => {
  const fullName = `${user.name ?? ""} ${user.lastName ?? ""}`.trim();
  return {
    id: user.id,
    photo: user.photo,
    initials: initialsOf(fullName),
    name: fullName,
    email: user.email,
    client: user.client?.name ?? "—",
    status: user.active ? "active" : "inactive",
    role: (user.roles ?? []).includes("ROLE_CUSTOMER_ADMIN") ? "Administrador" : "Cliente",
    lastLogin: user.lastLogin ? dayjs(user.lastLogin) : null,
    tone: toneFor(user.id),
  };
}));

const clients = computed(() => [...new Set(users.value.map(({ client }) => client))].filter((c) => c !== "—"));

const lastLoginLabel = (lastLogin) => (lastLogin ? lastLogin.format("DD/MM/YYYY HH:mm") : "Nunca");

const matchesLastLoginFilter = (lastLogin) => {
  if (!filters.value.lastLogin) return true;
  if (!lastLogin) return false;
  if (filters.value.lastLogin === "today") return lastLogin.isAfter(dayjs().startOf("day"));
  if (filters.value.lastLogin === "yesterday") {
    return lastLogin.isAfter(dayjs().subtract(1, "day").startOf("day")) && lastLogin.isBefore(dayjs().startOf("day"));
  }
  if (filters.value.lastLogin === "older") return lastLogin.isBefore(dayjs().subtract(1, "day").startOf("day"));
  return true;
};

const filteredUsers = computed(() => users.value.filter((user) =>
  (!filters.value.search || normalize(`${user.name} ${user.email}`).includes(normalize(filters.value.search))) &&
  (!filters.value.client || user.client === filters.value.client) &&
  (!filters.value.status || user.status === filters.value.status) &&
  (!filters.value.role || user.role === filters.value.role) &&
  matchesLastLoginFilter(user.lastLogin)
));
const listedUsers = computed(() => filteredUsers.value.slice((page.value - 1) * itemsPerPage.value, page.value * itemsPerPage.value));
const search = () => { page.value = 1; };
const openPermissions = (user) => {
  saveUserListState("external", { filters: filters.value, page: page.value, itemsPerPage: itemsPerPage.value });
  router.push({ name: "security-user-permissions", query: { user: user.id, type: "external" } });
};
const pending = (label) => Notification.info(`${label} estará disponible próximamente.`, 3500);

onMounted(() => {
  const stored = restoreUserListState("external");
  if (stored) {
    filters.value = { ...filters.value, ...stored.filters };
    page.value = stored.page || 1;
    itemsPerPage.value = stored.itemsPerPage || 10;
  }
  loadUsers();
});
</script>

<template>
  <section class="external-users">
    <HeaderTitle title="Seguridad" />
    <SecuritySectionTitle title="Usuarios externos" />
    <el-row class="mt-2">
      <el-card class="card-search external-users__filters w-100 wback" body-style="padding:0">
        <div class="search-form">
          <div class="row">
            <div class="col-4 el-col-xs-24">
              <div class="form-item"><label>Nombre o email</label><el-input v-model="filters.search" class="w-100"
                  placeholder="Buscar por nombre o email" clearable @keyup.enter="search" /></div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="form-item"><label class="w-100">Cliente</label><el-select v-model="filters.client"
                  class="w-100" placeholder="Todos" filterable clearable @change="search"><el-option
                    v-for="client in clients" :key="client" :label="client" :value="client" /></el-select></div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="form-item"><label class="w-100">Estado</label><el-select v-model="filters.status"
                  class="w-100" placeholder="Todos" clearable @change="search"><el-option label="Activo"
                    value="active" /><el-option label="Inactivo" value="inactive" /></el-select></div>
            </div>
          </div>
          <div class="row second-row">
            <div class="col-4 el-col-xs-24">
              <div class="form-item"><label class="w-100">Rol base</label><el-select v-model="filters.role"
                  class="w-100" placeholder="Todos" clearable @change="search"><el-option label="Cliente"
                    value="Cliente" /><el-option label="Administrador" value="Administrador" /></el-select></div>
            </div>
            <div class="col-4 el-col-xs-24">
              <div class="form-item"><label class="w-100">Último login</label><el-select v-model="filters.lastLogin"
                  class="w-100" placeholder="Todos" clearable @change="search"><el-option label="Hoy"
                    value="today" /><el-option label="Ayer" value="yesterday" /><el-option
                    label="Hace más de 2 días" value="older" /></el-select></div>
            </div>
            <div class="col-4 el-col-xs-24 external-users__actions">
              <div class="d-flex justify-content-center">
                <div class="col-md-6 el-col-xs-12"><button class="btn btn_red btn__new"
                    @click="pending('Invitar usuario externo')">NUEVO</button></div>
                <div class="col-md-6 el-col-xs-12"><button class="btn btn_yellow btn__search"
                    @click="search">BUSCAR</button></div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </el-row>
    <div class="table table-not-mobile external-users__table">
      <table>
        <thead>
          <tr>
            <th><font-awesome-icon icon="user" /> Nombre</th>
            <th>Cliente</th>
            <th>Estado</th>
            <th>Rol base</th>
            <th>Último login</th>
            <th>Acción</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in listedUsers" :key="user.id" class="pointer" @click="openPermissions(user)">
            <td>
              <div class="person"><SecurityUserAvatar :photo="user.photo" :initials="user.initials" :tone="user.tone" /><span><b>{{
                user.name }}</b><small>{{ user.email }}</small></span></div>
            </td>
            <td>{{ user.client }}</td>
            <td><span class="status" :class="user.status">{{ user.status === 'active' ? 'Activo' : 'Inactivo' }}</span>
            </td>
            <td>{{ user.role }}</td>
            <td>{{ lastLoginLabel(user.lastLogin) }}</td>
            <td>
              <div class="row-actions"><font-awesome-layers class="fa-2x" title="Gestionar permisos"
                  @click.stop="openPermissions(user)"><font-awesome-icon icon="circle"
                    style="color:#f7d20e" /><font-awesome-icon icon="plus" class="fa-inverse"
                    transform="shrink-6" /></font-awesome-layers><font-awesome-layers class="fa-2x"
                  title="Desactivar usuario" @click.stop="pending(`Desactivar ${user.name}`)"><font-awesome-icon
                    icon="circle" style="color:#f9151c" /><font-awesome-icon icon="times" class="fa-inverse"
                    transform="shrink-6" /></font-awesome-layers></div>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="!filteredUsers.length" class="empty">No se han encontrado usuarios con los filtros seleccionados.</p>
    </div>
    <div class="table-mobile external-users__cards"><el-card v-for="user in listedUsers" :key="user.id"
        class="external-user-card pointer" @click="openPermissions(user)"><button class="external-user-card__delete"
          title="Desactivar usuario"
          @click.stop="pending(`Desactivar ${user.name}`)"><font-awesome-layers><font-awesome-icon
              icon="circle" /><font-awesome-icon icon="times" class="fa-inverse"
              transform="shrink-6" /></font-awesome-layers></button>
        <div class="person"><SecurityUserAvatar :photo="user.photo" :initials="user.initials" :tone="user.tone" /><span><b>{{ user.name
        }}</b><small>{{ user.email }}</small></span></div>
        <p><b>Cliente:</b>{{ user.client }}</p>
        <p><b>Estado:</b><span class="status" :class="user.status">{{ user.status === 'active' ? 'Activo' : 'Inactivo'
        }}</span></p>
        <p><b>Rol base:</b>{{ user.role }}</p>
        <p><b>Último login:</b>{{ lastLoginLabel(user.lastLogin) }}</p>
      </el-card>
      <p v-if="!filteredUsers.length" class="empty">No se han encontrado usuarios con los filtros seleccionados.</p>
    </div>
    <SharedElementPagination v-model:current-page="page" v-model:page-size="itemsPerPage" :total="filteredUsers.length"
      :page-size-options="[10, 20, 50]" class="pagination" @size-change="page = 1" />
  </section>
</template>

<style lang="scss" scoped>
.external-users {
  color: #30385d;
}

.security-subtitle {
  align-items: center;
  display: flex;
  margin-bottom: 10px;

  .svg-inline--fa {
    color: #474ce6;
    font-size: 27px;
  }

  .title-text {
    color: #474ce6;
    font-size: 2.5em;
    margin-left: 1rem;
  }
}

.external-users__filters {
  background: #fff !important;
  background-image: none !important;
  border-radius: 15px;
}

.btn__search,
.btn__new {
  height: 3em;
  margin-top: 22px;
  width: 100%;
}

.btn__search {
  color: #474ce6 !important;
  font-weight: 800;
  margin-left: 10px;
}

.person {
  align-items: center;
  display: flex;
  gap: 10px;
}

.person small {
  display: block;
  font-size: .75rem;
  font-weight: 400;
  margin-top: 3px;
}

.external-users__table table {
  min-width: 900px;
}

.external-users__table th {
  text-transform: uppercase;
}

.external-users__table td {
  vertical-align: middle;
}

.status {
  border: 1px solid;
  border-radius: 15px;
  font-size: .75rem;
  padding: 3px 10px;
}

.active {
  background: #effbf4;
  border-color: #c9eed9;
  color: #279653;
}

.inactive {
  background: #fff7ef;
  border-color: #ffe0ba;
  color: #fb6b21;
}

.row-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.row-actions :deep(.fa-layers) {
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: center;
  margin: 16px 0 22px;
}

.empty {
  color: #7c83a2;
  text-align: center;
}

.external-users__cards {
  display: none;
}

@media (min-width:768px) {
  .table-mobile {
    display: none;
  }
}

@media (max-width:767px) {
  .table-not-mobile {
    display: none;
  }

  .external-users__filters {
    background: #fff !important;
  }

  .second-row {
    margin-top: 0;
  }

  .external-users__actions .d-flex {
    margin-bottom: 10px;
  }

  .btn__search {
    margin-left: 8px;
  }

  .external-users__cards {
    display: grid;
    gap: 12px;
  }

  .external-user-card {
    position: relative;
  }

  .external-user-card :deep(.el-card__body) {
    padding: 14px;
  }

  .external-user-card__delete {
    background: transparent;
    border: 0;
    color: #f9151c;
    font-size: 1.8rem;
    position: absolute;
    right: 8px;
    top: 5px;
  }

  .external-user-card p {
    display: grid;
    gap: 7px;
    grid-template-columns: 118px minmax(0, 1fr);
    margin: 10px 0 0;
    word-break: break-word;
  }

  .external-user-card p>b {
    color: #474ce6;
  }

  .pagination {
    overflow: hidden;
  }

  .security-subtitle .title-text {
    font-size: 2em;
  }
}
</style>
